import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mat } from "./WorldModels";

const TOWN_ROOT = "/assets/kenney-fantasy-town";

export interface TownPart {
  geometry: THREE.BufferGeometry;
  material: THREE.MeshLambertMaterial;
  matrix: THREE.Matrix4;
}

export interface TownTemplate {
  id: string;
  parts: TownPart[];
  height: number;
  /** Approx seat height for benches (world units before WORLD_PROP_SCALE). */
  seatY?: number;
}

export type TownFamily =
  | "fence"
  | "lantern"
  | "bench"
  | "cart"
  | "stall"
  | "fountain"
  | "hedge"
  | "road"
  | "curb"
  | "plank"
  | "pillar";

const FILES: Record<TownFamily, readonly string[]> = {
  fence: ["fence.glb", "fence-curved.glb", "fence-gate.glb", "fence-broken.glb"],
  lantern: ["lantern.glb"],
  bench: ["park-bench-holiday.glb"],
  cart: ["cart.glb", "cart-high.glb"],
  stall: ["stall.glb", "stall-green.glb", "stall-red.glb", "stall-stool.glb"],
  fountain: [
    "fountain-round.glb",
    "fountain-square.glb",
    "fountain-center.glb",
    "fountain-round-detail.glb",
  ],
  hedge: ["hedge.glb", "hedge-large.glb"],
  // Park sidewalks (modular cobble / curb from Fantasy Town Kit).
  road: ["road.glb", "road-bend.glb", "road-corner.glb", "road-corner-inner.glb", "road-edge.glb"],
  curb: ["road-curb.glb", "road-curb-end.glb"],
  // Pier decks / posts.
  plank: ["planks.glb", "planks-half.glb", "planks-opening.glb"],
  pillar: ["pillar-wood.glb"],
};

/** Soft warm tint over Kenney colormaps so props sit with Dogdell grass. */
const TOWN_TINT = 0xf2e6d4;

let ready = false;
let loadPromise: Promise<void> | null = null;

const catalog: Record<TownFamily, TownTemplate[]> = {
  fence: [],
  lantern: [],
  bench: [],
  cart: [],
  stall: [],
  fountain: [],
  hedge: [],
  road: [],
  curb: [],
  plank: [],
  pillar: [],
};

const mappedMatCache = new Map<string, THREE.MeshLambertMaterial>();

function matMapped(color: number, map: THREE.Texture | null): THREE.MeshLambertMaterial {
  if (!map) {
    return mat(color);
  }
  const key = `${color}:${map.uuid}`;
  let m = mappedMatCache.get(key);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color, map });
    m.userData.shared = true;
    mappedMatCache.set(key, m);
  }
  return m;
}

export function townTemplates(family: TownFamily): readonly TownTemplate[] {
  return catalog[family];
}

export function pickTownTemplate(family: TownFamily, variant = 0): TownTemplate | null {
  const list = catalog[family];
  if (list.length === 0) {
    return null;
  }
  return list[((variant % list.length) + list.length) % list.length]!;
}

function cloneFromTemplate(template: TownTemplate): THREE.Group {
  const group = new THREE.Group();
  group.name = `town-${template.id}`;
  for (const part of template.parts) {
    const mesh = new THREE.Mesh(part.geometry, part.material);
    mesh.matrix.copy(part.matrix);
    mesh.matrixAutoUpdate = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    group.add(mesh);
  }
  if (template.seatY != null) {
    group.userData.seatY = template.seatY;
  }
  return group;
}

/** Clone a Kenney Fantasy Town / Furniture prop (feet at y=0). */
export function cloneTownProp(family: TownFamily, variant = 0): THREE.Group | null {
  const template = pickTownTemplate(family, variant);
  if (!template) {
    return null;
  }
  return cloneFromTemplate(template);
}

/** Clone a specific Kenney piece by catalog id (e.g. `"road"` for the solid slab). */
export function cloneTownById(id: string): THREE.Group | null {
  for (const family of Object.keys(catalog) as TownFamily[]) {
    const template = catalog[family].find((t) => t.id === id);
    if (template) {
      return cloneFromTemplate(template);
    }
  }
  return null;
}

export function loadFantasyTownAssets(): Promise<void> {
  if (ready) {
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  loadPromise = (async () => {
    const loader = new GLTFLoader();
    const jobs: Promise<void>[] = [];
    for (const family of Object.keys(FILES) as TownFamily[]) {
      for (const file of FILES[family]) {
        jobs.push(loadOne(loader, file, family));
      }
    }
    await Promise.all(jobs);
    // Keep family order stable (parallel loads used to shuffle variant 0).
    for (const family of Object.keys(FILES) as TownFamily[]) {
      const order = new Map(FILES[family].map((file, i) => [file.replace(/\.glb$/i, ""), i]));
      catalog[family].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }
    ready = true;
  })();
  return loadPromise;
}

async function loadOne(
  loader: GLTFLoader,
  file: string,
  family: TownFamily,
): Promise<void> {
  try {
    const gltf = await loader.loadAsync(`${TOWN_ROOT}/${file}`);
    const root = gltf.scene;
    root.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(root);
    if (Number.isFinite(box.min.y)) {
      root.position.y -= box.min.y;
      root.updateMatrixWorld(true);
    }

    // Normalize Kenney meters toward park prop size (WORLD_PROP_SCALE applied later).
    // Flat road / plank / curb pieces fit by footprint; tall props fit by height.
    const height = Math.max(0.05, box.max.y - box.min.y);
    const span = Math.max(box.max.x - box.min.x, box.max.z - box.min.z, 0.2);
    const flat =
      family === "road" || family === "curb" || family === "plank";
    const targetH =
      family === "fence"
        ? 1.15
        : family === "lantern"
          ? 3.05
          : family === "bench"
            ? 0.95
            : family === "cart" || family === "stall"
              ? 1.45
              : family === "fountain"
                ? 1.35
                : family === "hedge"
                  ? 0.85
                  : family === "pillar"
                    ? 1.25
                    : 1.0;
    const targetSpan =
      family === "road" ? 1.08
      : family === "curb" ? 1.0
      : family === "plank" ? 1.05
      : 1.0;
    const fit = flat
      ? targetSpan / span
      : targetH / Math.max(0.2, height);
    root.scale.setScalar(fit);
    root.updateMatrixWorld(true);
    const fitted = new THREE.Box3().setFromObject(root);

    const parts: TownPart[] = [];
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || !obj.geometry) {
        return;
      }
      const srcMat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
      const map =
        srcMat && "map" in srcMat
          ? ((srcMat as THREE.MeshStandardMaterial).map ?? null)
          : null;
      if (map) {
        map.colorSpace = THREE.SRGBColorSpace;
        map.needsUpdate = true;
      }
      const geometry = obj.geometry.clone();
      geometry.userData.shared = true;
      parts.push({
        geometry,
        material: matMapped(
          map ? (family === "bench" ? 0xffffff : TOWN_TINT) : guessFlatColor(family, file),
          map,
        ),
        matrix: obj.matrixWorld.clone(),
      });
    });

    if (parts.length === 0) {
      return;
    }

    const finalH = Math.max(0.15, fitted.max.y - fitted.min.y);
    // Park-bench seat is roughly mid-shin on standing humans (~45% of backrest height).
    const seatY =
      family === "bench" ? Math.min(0.48, finalH * 0.45) : undefined;

    catalog[family].push({
      id: file.replace(/\.glb$/i, ""),
      parts,
      height: finalH,
      seatY,
    });
  } catch (err) {
    console.warn(`[fantasy-town] failed to load ${file}`, err);
  }
}

function guessFlatColor(family: TownFamily, file: string): number {
  if (family === "lantern") {
    return 0xfff3e0;
  }
  if (family === "bench") {
    // Holiday Kit orange seat / back (legs come from colormap).
    return 0xe8a05a;
  }
  if (family === "fence" || family === "pillar") {
    return 0xa1887f;
  }
  if (family === "plank") {
    return 0xb08968;
  }
  if (family === "road" || family === "curb") {
    return 0xcfc4b4;
  }
  if (family === "hedge") {
    return 0x86c858;
  }
  if (file.includes("red")) {
    return 0xe57373;
  }
  if (file.includes("green")) {
    return 0x81c784;
  }
  if (family === "fountain") {
    return 0x90a4ae;
  }
  return 0xbcaaa4;
}
